import React, { useState, useMemo, useEffect, useCallback } from "react";
import "./Report.scss";
import {
  getUserActivityReportApi,
  getRoomActivityReportApi,
  getWhatsappRoutingReportApi,
  getMediaStorageReportApi,
  getSecurityEventsReportApi,
  getBroadcastEngagementReportApi,
} from "../../../../api/api";

// ─── Report Catalog Config ────────────────────────────────────────────────────
const REPORT_CATALOG = [
  {
    id: "user_activity",
    name: "User Activity",
    description:
      "Active users, new users, inactive users, login frequency and session durations by department.",
    filters: ["Date Range", "Department", "Role", "Status"],
    exports: ["pdf", "excel"],
    color: "#0062FE",
    bg: "rgba(0,98,254,0.10)",
    tags: ["Users", "Sessions", "Access"],
  },
  {
    id: "room_activity",
    name: "Room Activity",
    description:
      "Messages sent, members, files shared, and active users per room, filterable by type.",
    filters: ["Date Range", "Room Type", "Department", "Status"],
    exports: ["pdf", "excel"],
    color: "#8B5CF6",
    bg: "rgba(139,92,246,0.10)",
    tags: ["Rooms", "Messages", "Files"],
  },
  {
    id: "whatsapp_routing",
    name: "WhatsApp Routing",
    description:
      "Inbound/outbound routing summary, failures, media count and routing accuracy rate.",
    filters: ["Date Range", "Phone Number", "Room", "Status", "Type"],
    exports: ["pdf", "excel"],
    color: "#22C55E",
    bg: "rgba(34,197,94,0.10)",
    tags: ["WhatsApp", "Routing", "Failures"],
  },
  {
    id: "media_storage",
    name: "Media Storage",
    description:
      "Storage breakdown by file type, room and age, with retention expiry flags.",
    filters: ["Date Range", "File Type", "Room", "Status"],
    exports: ["excel", "csv"],
    color: "#F97316",
    bg: "rgba(249,115,22,0.10)",
    tags: ["Storage", "Media", "Retention"],
  },
  {
    id: "security_events",
    name: "Security Events",
    description:
      "Failed logins, session resets, role changes, IP violations and critical audit events.",
    filters: ["Date Range", "Action", "User"],
    exports: ["pdf", "excel"],
    color: "#EF4444",
    bg: "rgba(239,68,68,0.10)",
    tags: ["Security", "Audit", "Risk"],
  },
  {
    id: "broadcast_engagement",
    name: "Broadcast Engagement",
    description:
      "Read rates, delivery success, and audience reach per broadcast announcement.",
    filters: ["Date Range", "Audience", "Status"],
    exports: ["pdf", "excel"],
    color: "#06B6D4",
    bg: "rgba(6,182,212,0.10)",
    tags: ["Broadcasts", "Engagement", "Delivery"],
  },
];

const DEPARTMENTS = [
  "All Departments",
  "Operations Command",
  "Port Security",
  "Finance Department",
  "IT Department",
  "Logistics",
];
const ROOMS = [
  "All Rooms",
  "Operations Command",
  "Port Security Updates",
  "IT Support",
  "Finance Department",
  "Logistics Team",
];
const SEVERITIES = ["All Severity", "Critical", "Warning", "Info"];
const FILE_TYPES = ["All Types", "PDF", "Image", "Excel", "Video", "Other"];
const WHATSAPP_STATUSES = ["All Status", "ROUTED", "FAILED", "REJECTED"];
const WHATSAPP_TYPES = ["All Types", "TEXT", "IMAGE", "VIDEO", "PDF", "AUDIO"];
const AUDIENCE_TYPES = ["All Audience", "ALL", "DEPARTMENT", "ROOM"];
const BROADCAST_STATUSES = ["All Status", "SENT", "SCHEDULED", "DRAFT"];
const MEDIA_STATUSES = ["All Status", "available", "quarantined"];
const USER_STATUSES = ["All Status", "ACTIVE", "INACTIVE"];
const ROOM_TYPES = ["All Types", "PRIVATE", "PUBLIC", "BROADCAST"];

const PAGE_SIZE = 10;

// ─── Icons ───────────────────────────────────────────────────────────────────
const Ico = {
  ChevR: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  SortUp: (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  ),
  Close: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Export: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  PDF: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  Excel: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18" />
    </svg>
  ),
  CSV: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  Bar: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <line x1="2" y1="20" x2="22" y2="20" />
    </svg>
  ),
  Users: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Shield: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Clock: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Play: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  Refresh: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  ),
  Info: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Loader: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ animation: "spin 1s linear infinite" }}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  ),
};

const REPORT_ICONS = {
  user_activity: Ico.Users,
  room_activity: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  whatsapp_routing: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  media_storage: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  security_events: Ico.Shield,
  broadcast_engagement: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  ),
};

// ─── Helper: format bytes ─────────────────────────────────────────────────────
const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// ─── Helper: format date ──────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ─── Table configs per report type ───────────────────────────────────────────
const TABLE_CONFIG = {
  user_activity: {
    headers: [
      "User",
      "Email",
      "Mobile",
      "Department",
      "Role",
      "Status",
      "Created At",
    ],
    rowMapper: (u) => [
      u.fullName || "—",
      u.email || "—",
      u.mobile || "—",
      u.department?.name || "—",
      u.role?.name || "—",
      u.status || "—",
      fmtDate(u.createdAt),
    ],
    statusCol: 5,
  },
  room_activity: {
    headers: [
      "Room",
      "Type",
      "Members",
      "Status",
      "Department",
      "Matrix Room ID",
      "Created At",
    ],
    rowMapper: (r) => [
      r.roomName || "—",
      r.roomType || "—",
      String(r.members?.length ?? 0),
      r.status || "—",
      r.department?.name || "—",
      r.matrixRoomId || "—",
      fmtDate(r.createdAt),
    ],
    statusCol: 3,
  },
  whatsapp_routing: {
    headers: [
      "Identity",
      "Message Type",
      "File Size",
      "Action",
      "Matrix Room",
      "Status",
      "Detail",
      "Created At",
    ],
    rowMapper: (l) => [
      l.whatsappIdentity || "—",
      l.messageType || "—",
      l.fileSize ? formatBytes(l.fileSize) : "—",
      l.action || "—",
      l.matrixRoomId ? l.matrixRoomId.split(":")[0] + ":…" : "—",
      l.status || "—",
      l.detail || "—",
      fmtDate(l.createdAt),
    ],
    statusCol: 5,
  },
  media_storage: {
    headers: [
      "File Name",
      "MIME Type",
      "Room ID",
      "MXC URL",
      "Size",
      "Status",
      "Created At",
    ],
    rowMapper: (m) => [
      m.fileName || "—",
      m.mimeType || "—",
      m.matrixRoomId || "—",
      m.matrixMxcUrl || "—",
      formatBytes(m.fileSize),
      m.status || "—",
      fmtDate(m.createdAt),
    ],
    statusCol: 5,
  },
  security_events: {
    headers: [
      "Action",
      "Entity Type",
      "Entity ID",
      "Actor",
      "IP Address",
      "Created At",
    ],
    rowMapper: (l) => [
      l.action || "—",
      l.entityType || "—",
      l.entityId || "—",
      l.actor?.fullName || "—",
      l.details?.ip || "—",
      fmtDate(l.createdAt),
    ],
    statusCol: -1,
  },
  broadcast_engagement: {
    headers: [
      "Title",
      "Message",
      "Audience",
      "Recipients",
      "Read",
      "Read Rate",
      "Created By",
      "Status",
    ],
    rowMapper: (b) => [
      b.title || "—",
      (b.message || "—").slice(0, 40) + (b.message?.length > 40 ? "…" : ""),
      b.audienceType || "—",
      String(b.recipients ?? 0),
      String(b.readCount ?? 0),
      `${b.readRate ?? 0}%`,
      b.createdBy?.fullName || "—",
      b.status || "—",
    ],
    statusCol: 7,
  },
};

// ─── API fetcher per report ───────────────────────────────────────────────────
const fetchReport = async (reportId, filters, page) => {
  const {
    fromDate = "",
    toDate = "",
    department = "",
    role = "",
    status = "",
    roomType = "",
    phone = "",
    type = "",
    room = "",
    fileType = "",
    action = "",
    user = "",
    audienceType = "",
  } = filters;

  switch (reportId) {
    case "user_activity":
      return getUserActivityReportApi(
        page,
        PAGE_SIZE,
        department,
        role,
        status,
        fromDate,
        toDate,
      );
    case "room_activity":
      return getRoomActivityReportApi(
        page,
        PAGE_SIZE,
        department,
        roomType,
        status,
        fromDate,
        toDate,
      );
    case "whatsapp_routing":
      return getWhatsappRoutingReportApi(
        page,
        PAGE_SIZE,
        status,
        type,
        phone,
        room,
        fromDate,
        toDate,
      );
    case "media_storage":
      return getMediaStorageReportApi(
        page,
        PAGE_SIZE,
        fileType,
        status,
        room,
        fromDate,
        toDate,
      );
    case "security_events":
      return getSecurityEventsReportApi(
        page,
        PAGE_SIZE,
        action,
        user,
        fromDate,
        toDate,
      );
    case "broadcast_engagement":
      return getBroadcastEngagementReportApi(
        page,
        PAGE_SIZE,
        audienceType,
        status,
        fromDate,
        toDate,
      );
    default:
      throw new Error("Unknown report");
  }
};

// ─── Extract rows + pagination from API response ──────────────────────────────
const extractResponse = (reportId, data) => {
  const base = { pagination: data.pagination, summary: data.summary || null };
  switch (reportId) {
    case "user_activity":
      return { ...base, rows: data.users || [] };
    case "room_activity":
      return { ...base, rows: data.rooms || [] };
    case "whatsapp_routing":
      return { ...base, rows: data.logs || [] };
    case "media_storage":
      return { ...base, rows: data.media || [] };
    case "security_events":
      return { ...base, rows: data.logs || [] };
    case "broadcast_engagement":
      return { ...base, rows: data.broadcasts || [] };
    default:
      return { ...base, rows: [] };
  }
};

// ─── Summary KPI pills ────────────────────────────────────────────────────────
function SummaryBar({ reportId, summary }) {
  if (!summary) return null;
  const items = [];
  if (reportId === "user_activity") {
    items.push(
      {
        label: "Total",
        value: summary.totalUsers,
        color: "#0062FE",
        bg: "rgba(0,98,254,.10)",
      },
      {
        label: "Active",
        value: summary.activeUsers,
        color: "#22C55E",
        bg: "rgba(34,197,94,.10)",
      },
      {
        label: "Inactive",
        value: summary.inactiveUsers,
        color: "#EF4444",
        bg: "rgba(239,68,68,.10)",
      },
    );
  } else if (reportId === "whatsapp_routing") {
    items.push(
      {
        label: "Total",
        value: summary.total,
        color: "#0062FE",
        bg: "rgba(0,98,254,.10)",
      },
      {
        label: "Routed",
        value: summary.routed,
        color: "#22C55E",
        bg: "rgba(34,197,94,.10)",
      },
      {
        label: "Failed",
        value: summary.failed,
        color: "#EF4444",
        bg: "rgba(239,68,68,.10)",
      },
      {
        label: "Rejected",
        value: summary.rejected,
        color: "#F97316",
        bg: "rgba(249,115,22,.10)",
      },
    );
  } else if (reportId === "media_storage") {
    items.push(
      {
        label: "Total Files",
        value: summary.totalFiles,
        color: "#F97316",
        bg: "rgba(249,115,22,.10)",
      },
      {
        label: "Total Size",
        value: formatBytes(summary.totalSizeBytes),
        color: "#8B5CF6",
        bg: "rgba(139,92,246,.10)",
      },
    );
  }
  if (!items.length) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        padding: "12px 20px",
        borderBottom: "1px solid #E5E7EB",
        background: "#F7F9FC",
      }}
    >
      {items.map((it) => (
        <div
          key={it.label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 14px",
            borderRadius: 8,
            background: it.bg,
            border: `1px solid ${it.color}22`,
          }}
        >
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              fontFamily: "Poppins, sans-serif",
              color: it.color,
            }}
          >
            {it.value ?? "—"}
          </span>
          <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>
            {it.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Shared chart primitives ──────────────────────────────────────────────────

/** Reusable donut chart */
function DonutChart({ slices, total, label, size = 140 }) {
  let angle = -90;
  const R = 70;
  const CX = 100;
  const CY = 100;
  const paths = slices
    .map((s) => {
      if (s.value <= 0) return null;
      const pct = s.value / total;
      const deg = pct * 360;
      const startA = (angle * Math.PI) / 180;
      const endA = ((angle + deg) * Math.PI) / 180;
      const x1 = CX + R * Math.cos(startA);
      const y1 = CY + R * Math.sin(startA);
      const x2 = CX + R * Math.cos(endA);
      const y2 = CY + R * Math.sin(endA);
      const large = deg > 180 ? 1 : 0;
      const path = `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
      angle += deg;
      return { ...s, path };
    })
    .filter(Boolean);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
      <svg viewBox="0 0 200 200" style={{ width: size, flexShrink: 0 }}>
        {paths.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} opacity="0.9">
            <title>
              {s.label}: {s.value}
            </title>
          </path>
        ))}
        <circle cx={CX} cy={CY} r="42" fill="#fff" />
        <text
          x={CX}
          y={CY - 4}
          textAnchor="middle"
          fontSize="15"
          fontWeight="700"
          fill="#071B33"
        >
          {total}
        </text>
        <text
          x={CX}
          y={CY + 13}
          textAnchor="middle"
          fontSize="10"
          fill="#9CA3AF"
        >
          {label}
        </text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {paths.map((s) => (
          <div
            key={s.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontSize: 12.5,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: s.color,
                flexShrink: 0,
              }}
            />
            <span style={{ color: "#374151" }}>{s.label}</span>
            <span
              style={{
                color: "#9CA3AF",
                marginLeft: "auto",
                paddingLeft: 14,
                fontWeight: 600,
              }}
            >
              {s.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Reusable horizontal bar chart — great for categorical counts */
function HBarChart({ entries, color = "#0062FE", maxVal }) {
  const max = maxVal || Math.max(...entries.map((e) => e.value), 1);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 9,
        width: "100%",
      }}
    >
      {entries.map((e) => (
        <div
          key={e.label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 12.5,
          }}
        >
          <div
            style={{
              width: 110,
              color: "#374151",
              flexShrink: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={e.label}
          >
            {e.label}
          </div>
          <div
            style={{
              flex: 1,
              height: 8,
              background: "#F3F4F6",
              borderRadius: 99,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${(e.value / max) * 100}%`,
                background: e.color || color,
                borderRadius: 99,
                transition: "width 0.5s cubic-bezier(.22,1,.36,1)",
              }}
            />
          </div>
          <span
            style={{
              minWidth: 30,
              textAlign: "right",
              fontWeight: 600,
              color: e.color || color,
              fontSize: 12,
            }}
          >
            {e.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Reusable vertical bar chart */
function VBarChart({ entries, title }) {
  const W = 520;
  const H = 180;
  const PAD = { top: 20, right: 12, bottom: 36, left: 44 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;
  const maxVal = Math.max(...entries.map((e) => e.value), 1);
  const barW = Math.min(48, cW / entries.length - 8);
  const groupW = cW / entries.length;
  const gridVals = [0, Math.round(maxVal * 0.5), maxVal];
  const yPos = (v) => PAD.top + cH - (v / maxVal) * cH;

  return (
    <div>
      {title && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: 10,
          }}
        >
          {title}
        </div>
      )}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", overflow: "visible" }}
      >
        {gridVals.map((v, i) => (
          <g key={i}>
            <line
              x1={PAD.left}
              y1={yPos(v)}
              x2={W - PAD.right}
              y2={yPos(v)}
              stroke="#F3F4F6"
              strokeDasharray="4 3"
            />
            <text
              x={PAD.left - 6}
              y={yPos(v) + 4}
              textAnchor="end"
              fontSize="10"
              fill="#9CA3AF"
            >
              {v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
            </text>
          </g>
        ))}
        {entries.map((e, gi) => {
          const gx = PAD.left + gi * groupW + groupW / 2;
          const bh = (e.value / maxVal) * cH;
          const by = PAD.top + cH - bh;
          const bx = gx - barW / 2;
          return (
            <g key={e.label}>
              <rect
                x={bx}
                y={by}
                width={barW}
                height={Math.max(3, bh)}
                fill={e.color || "#0062FE"}
                rx="4"
                opacity="0.88"
              >
                <title>
                  {e.label}: {e.value}
                </title>
              </rect>
              <text
                x={gx}
                y={PAD.top + cH + 14}
                textAnchor="middle"
                fontSize="10"
                fill="#9CA3AF"
              >
                {e.label.length > 8 ? e.label.slice(0, 7) + "…" : e.label}
              </text>
              <text
                x={gx}
                y={Math.max(PAD.top + 11, by - 4)}
                textAnchor="middle"
                fontSize="10"
                fill={e.color || "#0062FE"}
                fontWeight="700"
              >
                {e.value}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Chart panel wrapper ──────────────────────────────────────────────────────
function ChartPanel({ left, right, leftTitle, rightTitle }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 32,
        flexWrap: "wrap",
        alignItems: "flex-start",
      }}
    >
      <div style={{ minWidth: 220 }}>
        {leftTitle && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 12,
            }}
          >
            {leftTitle}
          </div>
        )}
        {left}
      </div>
      <div style={{ flex: 1, minWidth: 260 }}>
        {rightTitle && (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 12,
            }}
          >
            {rightTitle}
          </div>
        )}
        {right}
      </div>
    </div>
  );
}

// ─── Per-report chart components ──────────────────────────────────────────────

function UserActivityChart({ rows, summary }) {
  // Left: active vs inactive donut (from summary)
  const total = summary?.totalUsers || rows.length;
  const active =
    summary?.activeUsers ?? rows.filter((r) => r.status === "ACTIVE").length;
  const inactive =
    summary?.inactiveUsers ?? rows.filter((r) => r.status !== "ACTIVE").length;
  const donutSlices = [
    { label: "Active", value: active, color: "#22C55E" },
    { label: "Inactive", value: inactive, color: "#EF4444" },
  ];

  // Right: department distribution (from rows)
  const deptCounts = {};
  rows.forEach((r) => {
    const d = r.department?.name || "Unknown";
    deptCounts[d] = (deptCounts[d] || 0) + 1;
  });
  const deptEntries = Object.entries(deptCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([label, value], i) => ({
      label,
      value,
      color: [
        "#0062FE",
        "#22C55E",
        "#8B5CF6",
        "#F97316",
        "#06B6D4",
        "#EF4444",
        "#F59E0B",
        "#9CA3AF",
      ][i % 8],
    }));

  return (
    <ChartPanel
      leftTitle="Status Breakdown"
      left={<DonutChart slices={donutSlices} total={total} label="Users" />}
      rightTitle="By Department"
      right={
        deptEntries.length > 0 ? (
          <HBarChart entries={deptEntries} />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>
            No department data
          </div>
        )
      }
    />
  );
}

function RoomActivityChart({ rows }) {
  // Left: room type donut
  const typeCounts = {};
  rows.forEach((r) => {
    const t = r.roomType || "Unknown";
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  const TYPE_COLORS = {
    PRIVATE: "#8B5CF6",
    PUBLIC: "#0062FE",
    BROADCAST: "#22C55E",
    Unknown: "#9CA3AF",
  };
  const typeSlices = Object.entries(typeCounts).map(([label, value]) => ({
    label,
    value,
    color: TYPE_COLORS[label] || "#9CA3AF",
  }));
  const typeTotal = typeSlices.reduce((s, d) => s + d.value, 0);

  // Right: status donut (active vs inactive)
  const statusCounts = {};
  rows.forEach((r) => {
    const s = r.status || "Unknown";
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });
  const STATUS_COLORS = {
    ACTIVE: "#22C55E",
    INACTIVE: "#EF4444",
    Unknown: "#9CA3AF",
  };
  const statusEntries = Object.entries(statusCounts).map(
    ([label, value], i) => ({
      label,
      value,
      color: STATUS_COLORS[label] || "#9CA3AF",
    }),
  );

  return (
    <ChartPanel
      leftTitle="Room Type Distribution"
      left={
        typeSlices.length > 0 ? (
          <DonutChart slices={typeSlices} total={typeTotal} label="Rooms" />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No data</div>
        )
      }
      rightTitle="Status Breakdown"
      right={
        statusEntries.length > 0 ? (
          <HBarChart entries={statusEntries} />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No status data</div>
        )
      }
    />
  );
}

function WhatsappRoutingChart({ rows, summary }) {
  // Left: routing status donut from summary
  const statusSlices = summary
    ? [
        { label: "Routed", value: summary.routed || 0, color: "#22C55E" },
        { label: "Rejected", value: summary.rejected || 0, color: "#F97316" },
        { label: "Failed", value: summary.failed || 0, color: "#EF4444" },
      ].filter((d) => d.value > 0)
    : [];
  const statusTotal = statusSlices.reduce((s, d) => s + d.value, 0);

  // Right: message type vertical bars from rows
  const typeCounts = {};
  rows.forEach((r) => {
    const t = (r.messageType || "unknown").toLowerCase();
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  const TYPE_COLORS = {
    image: "#0062FE",
    video: "#8B5CF6",
    text: "#22C55E",
    audio: "#F97316",
    document: "#06B6D4",
    unknown: "#9CA3AF",
  };
  const typeEntries = Object.entries(typeCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([label, value]) => ({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      value,
      color: TYPE_COLORS[label] || "#9CA3AF",
    }));

  return (
    <ChartPanel
      leftTitle="Routing Status"
      left={
        statusTotal > 0 ? (
          <DonutChart slices={statusSlices} total={statusTotal} label="Total" />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No summary data</div>
        )
      }
      rightTitle="Message Type Breakdown"
      right={
        typeEntries.length > 0 ? (
          <VBarChart entries={typeEntries} />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>
            No message type data
          </div>
        )
      }
    />
  );
}

function MediaStorageChart({ rows, summary }) {
  // Left: file type donut from rows
  const typeCounts = {};
  const typeBytes = {};
  rows.forEach((r) => {
    const t = (r.mimeType || "other").split("/")[0] || "other";
    typeCounts[t] = (typeCounts[t] || 0) + 1;
    typeBytes[t] = (typeBytes[t] || 0) + (r.fileSize || 0);
  });
  const MIME_COLORS = {
    image: "#0062FE",
    video: "#8B5CF6",
    application: "#F97316",
    text: "#22C55E",
    audio: "#06B6D4",
    other: "#9CA3AF",
  };
  const typeSlices = Object.entries(typeCounts).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1),
    value,
    color: MIME_COLORS[label] || "#9CA3AF",
  }));
  const typeTotal = typeSlices.reduce((s, d) => s + d.value, 0);

  // Right: size by type horizontal bars
  const sizeEntries = Object.entries(typeBytes)
    .sort((a, b) => b[1] - a[1])
    .map(([label, bytes], i) => ({
      label: label.charAt(0).toUpperCase() + label.slice(1),
      value: Math.round(bytes / 1024),
      color: MIME_COLORS[label] || "#9CA3AF",
    }));

  return (
    <ChartPanel
      leftTitle="Files by Type"
      left={
        typeSlices.length > 0 ? (
          <DonutChart slices={typeSlices} total={typeTotal} label="Files" />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No data</div>
        )
      }
      rightTitle="Size by Type (KB)"
      right={
        sizeEntries.length > 0 ? (
          <HBarChart entries={sizeEntries} />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No size data</div>
        )
      }
    />
  );
}

function SecurityEventsChart({ rows }) {
  // Left: action type distribution
  const actionCounts = {};
  rows.forEach((r) => {
    const a = r.action || "UNKNOWN";
    actionCounts[a] = (actionCounts[a] || 0) + 1;
  });
  const ACTION_COLORS = [
    "#EF4444",
    "#F97316",
    "#F59E0B",
    "#0062FE",
    "#8B5CF6",
    "#06B6D4",
    "#22C55E",
    "#9CA3AF",
  ];
  const actionEntries = Object.entries(actionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([label, value], i) => ({
      label,
      value,
      color: ACTION_COLORS[i % ACTION_COLORS.length],
    }));
  const actionTotal = actionEntries.reduce((s, d) => s + d.value, 0);

  // Right: entity type distribution
  const entityCounts = {};
  rows.forEach((r) => {
    const e = r.entityType || "UNKNOWN";
    entityCounts[e] = (entityCounts[e] || 0) + 1;
  });
  const entityEntries = Object.entries(entityCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([label, value], i) => ({
      label,
      value,
      color: ACTION_COLORS[i % ACTION_COLORS.length],
    }));

  return (
    <ChartPanel
      leftTitle="Event Actions"
      left={
        actionEntries.length > 0 ? (
          <DonutChart
            slices={actionEntries}
            total={actionTotal}
            label="Events"
            size={130}
          />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No data</div>
        )
      }
      rightTitle="By Entity Type"
      right={
        entityEntries.length > 0 ? (
          <HBarChart entries={entityEntries} color="#EF4444" />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No entity data</div>
        )
      }
    />
  );
}

function BroadcastEngagementChart({ rows }) {
  // Left: status donut
  const statusCounts = {};
  rows.forEach((r) => {
    const s = r.status || "UNKNOWN";
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });
  const STATUS_COLORS = {
    SENT: "#22C55E",
    SCHEDULED: "#F59E0B",
    DRAFT: "#9CA3AF",
    FAILED: "#EF4444",
  };
  const statusSlices = Object.entries(statusCounts).map(([label, value]) => ({
    label,
    value,
    color: STATUS_COLORS[label] || "#06B6D4",
  }));
  const statusTotal = statusSlices.reduce((s, d) => s + d.value, 0);

  // Right: read rate vertical bars per broadcast
  const readEntries = rows
    .filter((r) => r.title && r.readRate != null)
    .map((r) => ({
      label: r.title,
      value: Math.round(r.readRate || 0),
      color: "#06B6D4",
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  return (
    <ChartPanel
      leftTitle="Broadcast Status"
      left={
        statusSlices.length > 0 ? (
          <DonutChart
            slices={statusSlices}
            total={statusTotal}
            label="Broadcasts"
          />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>No data</div>
        )
      }
      rightTitle="Read Rate % by Broadcast"
      right={
        readEntries.length > 0 ? (
          <VBarChart entries={readEntries} />
        ) : (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>
            No read rate data
          </div>
        )
      }
    />
  );
}

// ─── Chart dispatcher ─────────────────────────────────────────────────────────
function ReportChart({ reportId, rows, summary }) {
  switch (reportId) {
    case "user_activity":
      return <UserActivityChart rows={rows} summary={summary} />;
    case "room_activity":
      return <RoomActivityChart rows={rows} />;
    case "whatsapp_routing":
      return <WhatsappRoutingChart rows={rows} summary={summary} />;
    case "media_storage":
      return <MediaStorageChart rows={rows} summary={summary} />;
    case "security_events":
      return <SecurityEventsChart rows={rows} />;
    case "broadcast_engagement":
      return <BroadcastEngagementChart rows={rows} />;
    default:
      return null;
  }
}

// ─── Run Report Drawer ────────────────────────────────────────────────────────
function RunDrawer({ report, onClose, onRun }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filters, setFilters] = useState({});
  const [format, setFormat] = useState(report.exports[0] || "pdf");

  const setF = (key, val) => setFilters((p) => ({ ...p, [key]: val }));

  const handleRun = () => {
    onRun({
      report,
      filters: { fromDate: dateFrom, toDate: dateTo, ...filters },
    });
    onClose();
  };

  const setPreset = (days) => {
    const d = new Date();
    const f = new Date(d);
    f.setDate(d.getDate() - days);
    setDateTo(d.toISOString().split("T")[0]);
    setDateFrom(f.toISOString().split("T")[0]);
  };

  return (
    <>
      <div className="rp-drawer-overlay" onClick={onClose} />
      <div className="rp-drawer">
        <div className="rp-drawer__header">
          <div>
            <h3 className="rp-drawer__title">Run Report</h3>
            <p className="rp-drawer__subtitle">
              {report.name} — configure filters before export
            </p>
          </div>
          <button className="rp-drawer__close" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>

        <div className="rp-drawer__body">
          {/* Date Range */}
          <div className="rp-form-section">
            <span className="rp-form-section__label">
              Date Range <span style={{ color: "#EF4444" }}>*</span>
            </span>
            <div className="rp-form-row">
              <div className="rp-field">
                <label>From</label>
                <input
                  className="rp-input"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="rp-field">
                <label>To</label>
                <input
                  className="rp-input"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              {[
                [7, "Last 7 days"],
                [30, "Last 30 days"],
                [90, "Last 90 days"],
              ].map(([d, l]) => (
                <button
                  key={d}
                  className="rp-btn rp-btn--secondary"
                  style={{ fontSize: 12, padding: "5px 12px" }}
                  onClick={() => setPreset(d)}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Scope Filters */}
          <div className="rp-form-section">
            <span className="rp-form-section__label">Scope Filters</span>
            <div className="rp-form-row">
              {report.filters.includes("Department") && (
                <div className="rp-field">
                  <label>Department</label>
                  <select
                    className="rp-select"
                    onChange={(e) =>
                      setF(
                        "department",
                        e.target.value === "All Departments"
                          ? ""
                          : e.target.value,
                      )
                    }
                  >
                    {DEPARTMENTS.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>
              )}
              {report.filters.includes("Role") && (
                <div className="rp-field">
                  <label>Role</label>
                  <input
                    className="rp-input"
                    placeholder="e.g. ADMIN"
                    onChange={(e) => setF("role", e.target.value)}
                  />
                </div>
              )}
              {report.filters.includes("Status") &&
                report.id === "user_activity" && (
                  <div className="rp-field">
                    <label>Status</label>
                    <select
                      className="rp-select"
                      onChange={(e) =>
                        setF(
                          "status",
                          e.target.value === "All Status" ? "" : e.target.value,
                        )
                      }
                    >
                      {USER_STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
              {report.filters.includes("Room Type") && (
                <div className="rp-field">
                  <label>Room Type</label>
                  <select
                    className="rp-select"
                    onChange={(e) =>
                      setF(
                        "roomType",
                        e.target.value === "All Types" ? "" : e.target.value,
                      )
                    }
                  >
                    {ROOM_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}
              {report.filters.includes("Status") &&
                report.id === "room_activity" && (
                  <div className="rp-field">
                    <label>Status</label>
                    <select
                      className="rp-select"
                      onChange={(e) =>
                        setF(
                          "status",
                          e.target.value === "All Status" ? "" : e.target.value,
                        )
                      }
                    >
                      {USER_STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
              {report.filters.includes("Phone Number") && (
                <div className="rp-field">
                  <label>Phone</label>
                  <input
                    className="rp-input"
                    placeholder="e.g. 919876"
                    onChange={(e) => setF("phone", e.target.value)}
                  />
                </div>
              )}
              {report.filters.includes("Type") && (
                <div className="rp-field">
                  <label>Message Type</label>
                  <select
                    className="rp-select"
                    onChange={(e) =>
                      setF(
                        "type",
                        e.target.value === "All Types" ? "" : e.target.value,
                      )
                    }
                  >
                    {WHATSAPP_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}
              {report.filters.includes("Status") &&
                report.id === "whatsapp_routing" && (
                  <div className="rp-field">
                    <label>Status</label>
                    <select
                      className="rp-select"
                      onChange={(e) =>
                        setF(
                          "status",
                          e.target.value === "All Status" ? "" : e.target.value,
                        )
                      }
                    >
                      {WHATSAPP_STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
              {report.filters.includes("Room") && (
                <div className="rp-field">
                  <label>Room</label>
                  <select
                    className="rp-select"
                    onChange={(e) =>
                      setF(
                        "room",
                        e.target.value === "All Rooms" ? "" : e.target.value,
                      )
                    }
                  >
                    {ROOMS.map((r) => (
                      <option key={r}>{r}</option>
                    ))}
                  </select>
                </div>
              )}
              {report.filters.includes("File Type") && (
                <div className="rp-field">
                  <label>File Type</label>
                  <select
                    className="rp-select"
                    onChange={(e) =>
                      setF(
                        "fileType",
                        e.target.value === "All Types"
                          ? ""
                          : e.target.value.toLowerCase(),
                      )
                    }
                  >
                    {FILE_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}
              {report.filters.includes("Status") &&
                report.id === "media_storage" && (
                  <div className="rp-field">
                    <label>Status</label>
                    <select
                      className="rp-select"
                      onChange={(e) =>
                        setF(
                          "status",
                          e.target.value === "All Status" ? "" : e.target.value,
                        )
                      }
                    >
                      {MEDIA_STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
              {report.filters.includes("Action") && (
                <div className="rp-field">
                  <label>Action</label>
                  <input
                    className="rp-input"
                    placeholder="e.g. LOGIN"
                    onChange={(e) => setF("action", e.target.value)}
                  />
                </div>
              )}
              {report.filters.includes("User") && (
                <div className="rp-field">
                  <label>User</label>
                  <input
                    className="rp-input"
                    placeholder="username or id"
                    onChange={(e) => setF("user", e.target.value)}
                  />
                </div>
              )}
              {report.filters.includes("Audience") && (
                <div className="rp-field">
                  <label>Audience Type</label>
                  <select
                    className="rp-select"
                    onChange={(e) =>
                      setF(
                        "audienceType",
                        e.target.value === "All Audience" ? "" : e.target.value,
                      )
                    }
                  >
                    {AUDIENCE_TYPES.map((a) => (
                      <option key={a}>{a}</option>
                    ))}
                  </select>
                </div>
              )}
              {report.filters.includes("Status") &&
                report.id === "broadcast_engagement" && (
                  <div className="rp-field">
                    <label>Status</label>
                    <select
                      className="rp-select"
                      onChange={(e) =>
                        setF(
                          "status",
                          e.target.value === "All Status" ? "" : e.target.value,
                        )
                      }
                    >
                      {BROADCAST_STATUSES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
            </div>
          </div>

          {/* Export Format */}
          <div className="rp-form-section">
            <span className="rp-form-section__label">Export Format</span>
            <div className="rp-format-row">
              {report.exports.includes("pdf") && (
                <button
                  className={`rp-format-btn${format === "pdf" ? " active" : ""}`}
                  onClick={() => setFormat("pdf")}
                >
                  {Ico.PDF} PDF
                </button>
              )}
              {report.exports.includes("excel") && (
                <button
                  className={`rp-format-btn${format === "excel" ? " active" : ""}`}
                  onClick={() => setFormat("excel")}
                >
                  {Ico.Excel} Excel
                </button>
              )}
              {report.exports.includes("csv") && (
                <button
                  className={`rp-format-btn${format === "csv" ? " active" : ""}`}
                  onClick={() => setFormat("csv")}
                >
                  {Ico.CSV} CSV
                </button>
              )}
            </div>
          </div>

          <div className="rp-scheduled-note">
            {Ico.Info}
            <span>
              Exports include: generated by, generated at, applied filters and
              admin watermark.
            </span>
          </div>
        </div>

        <div className="rp-drawer__footer">
          <button
            className="rp-btn rp-btn--primary"
            style={{ flex: 1 }}
            onClick={handleRun}
          >
            {Ico.Play} Run & Export
          </button>
          <button className="rp-btn rp-btn--secondary" onClick={handleRun}>
            {Ico.Refresh} Preview
          </button>
          <button className="rp-btn rp-btn--ghost" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Report Viewer Section ────────────────────────────────────────────────────
function ReportViewer({ report, initialFilters, onClose, onExport }) {
  const cfg = TABLE_CONFIG[report.id];
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters || {});
  const [sortKey, setSortKey] = useState(0);
  const [sortDir, setSortDir] = useState("asc");
  const [chartTab, setChartTab] = useState("chart");
  const hasChart = true; // all reports have charts

  const loadData = useCallback(async (reportId, activeFilters, activePage) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchReport(reportId, activeFilters, activePage);
      const extracted = extractResponse(reportId, res.data);
      setRows(extracted.rows);
      setPagination(extracted.pagination);
      setSummary(extracted.summary);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load report data.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(report.id, filters, page);
  }, [report.id, filters, page, loadData]);

  const handleFilterChange = (key, val) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
    setPage(1);
  };

  const mappedRows = useMemo(() => rows.map(cfg.rowMapper), [rows, cfg]);

  const sorted = useMemo(() => {
    return [...mappedRows].sort((a, b) => {
      const aV = a[sortKey] ?? "";
      const bV = b[sortKey] ?? "";
      return sortDir === "asc"
        ? String(aV).localeCompare(String(bV))
        : String(bV).localeCompare(String(aV));
    });
  }, [mappedRows, sortKey, sortDir]);

  const totalPages = pagination?.totalPages ?? 1;

  const handleSort = (idx) => {
    if (sortKey === idx) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(idx);
      setSortDir("asc");
    }
  };

  const statusCls = (v) => {
    if (!v) return "";
    const map = {
      active: "active",
      available: "active",
      routed: "active",
      sent: "active",
      scheduled: "warning",
      draft: "warning",
      quarantined: "critical",
      inactive: "inactive",
      failed: "critical",
      rejected: "inactive",
      login_failed: "critical",
      login_success: "active",
    };
    return map[v.toLowerCase()] || "active";
  };

  return (
    <div className="rp-viewer">
      <div className="rp-viewer__header">
        <div
          className="rp-viewer__icon"
          style={{ background: report.bg, color: report.color }}
        >
          {REPORT_ICONS[report.id]}
        </div>
        <div className="rp-viewer__info">
          <div className="rp-viewer__title">{report.name} Report</div>
          <div className="rp-viewer__subtitle">
            {loading
              ? "Loading…"
              : pagination
                ? `${pagination.total} total records · Page ${pagination.page} of ${pagination.totalPages}`
                : "Live data from API"}
          </div>
        </div>
        <div className="rp-viewer__actions">
          <button
            className="rp-btn rp-btn--secondary"
            onClick={() => onExport("pdf")}
          >
            {Ico.PDF} PDF
          </button>
          <button
            className="rp-btn rp-btn--secondary"
            onClick={() => onExport("excel")}
          >
            {Ico.Excel} Excel
          </button>
          <button
            className="rp-btn rp-btn--ghost"
            onClick={() => loadData(report.id, filters, page)}
            title="Refresh"
          >
            {Ico.Refresh}
          </button>
          <button className="rp-btn rp-btn--ghost" onClick={onClose}>
            {Ico.Close}
          </button>
        </div>
      </div>

      {/* Inline Filters */}
      <div className="rp-viewer__filters">
        <div>
          <span className="rp-filter-label">Date From</span>
          <input
            type="date"
            className="rp-filter-date"
            value={filters.fromDate || ""}
            onChange={(e) => handleFilterChange("fromDate", e.target.value)}
          />
        </div>
        <div>
          <span className="rp-filter-label">Date To</span>
          <input
            type="date"
            className="rp-filter-date"
            value={filters.toDate || ""}
            onChange={(e) => handleFilterChange("toDate", e.target.value)}
          />
        </div>
        {report.filters.includes("Department") && (
          <div>
            <span className="rp-filter-label">Department</span>
            <select
              className="rp-filter-select"
              value={filters.department || ""}
              onChange={(e) =>
                handleFilterChange(
                  "department",
                  e.target.value === "All Departments" ? "" : e.target.value,
                )
              }
            >
              {DEPARTMENTS.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
          </div>
        )}
        {report.filters.includes("Status") && (
          <div>
            <span className="rp-filter-label">Status</span>
            <select
              className="rp-filter-select"
              value={filters.status || ""}
              onChange={(e) =>
                handleFilterChange(
                  "status",
                  e.target.value === "All Status" ? "" : e.target.value,
                )
              }
            >
              {(report.id === "whatsapp_routing"
                ? WHATSAPP_STATUSES
                : report.id === "media_storage"
                  ? MEDIA_STATUSES
                  : report.id === "broadcast_engagement"
                    ? BROADCAST_STATUSES
                    : USER_STATUSES
              ).map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
        )}
        {report.filters.includes("Room Type") && (
          <div>
            <span className="rp-filter-label">Room Type</span>
            <select
              className="rp-filter-select"
              value={filters.roomType || ""}
              onChange={(e) =>
                handleFilterChange(
                  "roomType",
                  e.target.value === "All Types" ? "" : e.target.value,
                )
              }
            >
              {ROOM_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
        )}
        {report.filters.includes("Type") && (
          <div>
            <span className="rp-filter-label">Msg Type</span>
            <select
              className="rp-filter-select"
              value={filters.type || ""}
              onChange={(e) =>
                handleFilterChange(
                  "type",
                  e.target.value === "All Types" ? "" : e.target.value,
                )
              }
            >
              {WHATSAPP_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
        )}
        {report.filters.includes("File Type") && (
          <div>
            <span className="rp-filter-label">File Type</span>
            <select
              className="rp-filter-select"
              value={filters.fileType || ""}
              onChange={(e) =>
                handleFilterChange(
                  "fileType",
                  e.target.value === "All Types"
                    ? ""
                    : e.target.value.toLowerCase(),
                )
              }
            >
              {FILE_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
        )}
        {report.filters.includes("Audience") && (
          <div>
            <span className="rp-filter-label">Audience</span>
            <select
              className="rp-filter-select"
              value={filters.audienceType || ""}
              onChange={(e) =>
                handleFilterChange(
                  "audienceType",
                  e.target.value === "All Audience" ? "" : e.target.value,
                )
              }
            >
              {AUDIENCE_TYPES.map((a) => (
                <option key={a}>{a}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Summary Bar */}
      <SummaryBar reportId={report.id} summary={summary} />

      {/* Table / Chart area */}
      <div className="rp-chart-area">
        {/* Chart/Table tabs — only shown for reports that have a chart */}
        {hasChart && !loading && !error && rows.length > 0 && (
          <div className="rp-chart-area__tabs">
            <button
              className={chartTab === "chart" ? "active" : ""}
              onClick={() => setChartTab("chart")}
            >
              Chart
            </button>
            <button
              className={chartTab === "table" ? "active" : ""}
              onClick={() => setChartTab("table")}
            >
              Table
            </button>
          </div>
        )}

        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "48px 0",
              color: "#6B7280",
            }}
          >
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            {Ico.Loader}{" "}
            <span style={{ fontSize: 14 }}>Loading report data…</span>
          </div>
        )}

        {!loading && error && (
          <div className="rp-empty">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EF4444"
              strokeWidth="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h4>Error Loading Data</h4>
            <p>{error}</p>
            <button
              className="rp-btn rp-btn--primary"
              onClick={() => loadData(report.id, filters, page)}
            >
              {Ico.Refresh} Retry
            </button>
          </div>
        )}

        {!loading && !error && sorted.length === 0 && (
          <div className="rp-empty">
            {Ico.Bar}
            <h4>No data found</h4>
            <p>Try adjusting the filters or date range.</p>
          </div>
        )}

        {!loading && !error && sorted.length > 0 && (
          <>
            {/* Chart view */}
            {hasChart && chartTab === "chart" && (
              <ReportChart reportId={report.id} rows={rows} summary={summary} />
            )}

            {/* Table view — shown when no chart, or chart tab is "table" */}
            {(!hasChart || chartTab === "table") && (
              <>
                <div className="rp-data-table__wrap">
                  <table className="rp-data-table">
                    <thead>
                      <tr>
                        {cfg.headers.map((h, i) => (
                          <th
                            key={i}
                            className={sortKey === i ? "sorted" : ""}
                            onClick={() => handleSort(i)}
                          >
                            {h}
                            <span
                              className={`sort-icon${sortKey === i ? " active" : ""}`}
                            >
                              {Ico.SortUp}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => (
                            <td key={ci}>
                              {ci === cfg.statusCol ? (
                                <span
                                  className={`rp-badge rp-badge--${statusCls(cell)}`}
                                >
                                  {cell}
                                </span>
                              ) : (
                                <span style={{ fontSize: 13 }}>{cell}</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="rp-pagination">
                  <div className="rp-pagination__info">
                    Page {page} of {totalPages} ·{" "}
                    {pagination?.total ?? sorted.length} total records
                  </div>
                  <div className="rp-pagination__controls">
                    <button
                      className="rp-pagination__page"
                      disabled={page === 1}
                      onClick={() => setPage(1)}
                    >
                      &laquo;
                    </button>
                    <button
                      className="rp-pagination__page"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      {Ico.ChevR}
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const p =
                        Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                      return (
                        <button
                          key={p}
                          className={`rp-pagination__page${page === p ? " active" : ""}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button
                      className="rp-pagination__page"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {Ico.ChevR}
                    </button>
                    <button
                      className="rp-pagination__page"
                      disabled={page === totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const Report = () => {
  const [activeReport, setActiveReport] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});
  const [runDrawer, setRunDrawer] = useState(null);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return REPORT_CATALOG;
    const q = search.toLowerCase();
    return REPORT_CATALOG.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [search]);

  const handleRun = ({ report, filters }) => {
    setActiveFilters(filters);
    setActiveReport(report);
  };

  const handleExport = (fmt) =>
    alert(`Exporting current report as ${fmt.toUpperCase()}…`);

  return (
    <div className="rp-page">
      {/* ── Header ── */}
      <div className="rp-header">
        <div className="rp-header__left">
          <span className="rp-header__label">Analytics</span>
          <h1 className="rp-header__title">Reports</h1>
          <p className="rp-header__subtitle">
            Exportable operational and compliance reports for management review
          </p>
        </div>
        {/* <div className="rp-header__right">
          <button className="rp-btn rp-btn--secondary">
            {Ico.Clock} Scheduled Reports
          </button>
          <button className="rp-btn rp-btn--primary">
            {Ico.Bar} Generate Report
          </button>
        </div>
        */}
      </div>

      {/* ── KPIs ── */}
      <div className="rp-kpi-grid">
        {[
          {
            label: "Report Types",
            value: REPORT_CATALOG.length,
            trend: "+1",
            dir: "up",
            color: "#0062FE",
            bg: "rgba(0,98,254,0.10)",
            icon: Ico.Bar,
          },
          {
            label: "Reports Generated",
            value: "142",
            trend: "+18",
            dir: "up",
            color: "#22C55E",
            bg: "rgba(34,197,94,0.10)",
            icon: Ico.Play,
          },
          {
            label: "Exports This Week",
            value: "24",
            trend: "+6",
            dir: "up",
            color: "#8B5CF6",
            bg: "rgba(139,92,246,0.10)",
            icon: Ico.Export,
          },
          {
            label: "Scheduled Reports",
            value: "3",
            trend: "0",
            dir: "flat",
            color: "#F97316",
            bg: "rgba(249,115,22,0.10)",
            icon: Ico.Clock,
          },
        ].map((k) => (
          <div key={k.label} className="rp-kpi">
            <div
              className="rp-kpi__icon"
              style={{ background: k.bg, color: k.color }}
            >
              {k.icon}
            </div>
            <div className="rp-kpi__body">
              <div className="rp-kpi__value">{k.value}</div>
              <div className="rp-kpi__label">{k.label}</div>
            </div>
            <span className={`rp-kpi__trend rp-kpi__trend--${k.dir}`}>
              {k.trend}
            </span>
          </div>
        ))}
      </div>

      {/* ── Active Report Viewer ── */}
      {activeReport && (
        <ReportViewer
          report={activeReport}
          initialFilters={activeFilters}
          onClose={() => setActiveReport(null)}
          onExport={handleExport}
        />
      )}

      {/* ── Report Catalog ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div className="rp-section-title" style={{ margin: 0 }}>
          Report Catalog
        </div>
        <div style={{ position: "relative", maxWidth: 280 }}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            style={{
              padding: "8px 14px 8px 36px",
              border: "1.5px solid #E5E7EB",
              borderRadius: 8,
              fontSize: 13,
              fontFamily: "Inter, sans-serif",
              outline: "none",
              width: 280,
              background: "#fff",
            }}
            placeholder="Search reports…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div
          className="rp-page"
          style={{
            background: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 12,
          }}
        >
          <div className="rp-empty">
            {Ico.Bar}
            <h4>No reports found</h4>
            <p>Try a different search term.</p>
          </div>
        </div>
      ) : (
        <div className="rp-catalog">
          {filtered.map((report) => (
            <div
              key={report.id}
              className="rp-catalog-card"
              style={{ "--rp-accent": report.color }}
              onClick={() => {
                setActiveFilters({});
                setActiveReport(report);
              }}
            >
              <div className="rp-catalog-card__body">
                <div className="rp-catalog-card__top">
                  <div
                    className="rp-catalog-card__icon"
                    style={{ background: report.bg, color: report.color }}
                  >
                    {REPORT_ICONS[report.id]}
                  </div>
                  <div className="rp-catalog-card__info">
                    <div className="rp-catalog-card__name">{report.name}</div>
                    <div className="rp-catalog-card__desc">
                      {report.description}
                    </div>
                  </div>
                </div>
                <div className="rp-catalog-card__tags">
                  {report.tags.map((t) => (
                    <span key={t} className="rp-catalog-card__tag">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="rp-catalog-card__footer">
                  <div className="rp-catalog-card__exports">
                    {report.exports.includes("pdf") && (
                      <span className="rp-catalog-card__export-chip rp-catalog-card__export-chip--pdf">
                        PDF
                      </span>
                    )}
                    {report.exports.includes("excel") && (
                      <span className="rp-catalog-card__export-chip rp-catalog-card__export-chip--excel">
                        Excel
                      </span>
                    )}
                    {report.exports.includes("csv") && (
                      <span className="rp-catalog-card__export-chip rp-catalog-card__export-chip--csv">
                        CSV
                      </span>
                    )}
                  </div>
                  <button
                    className="rp-catalog-card__run"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRunDrawer(report);
                    }}
                  >
                    {Ico.Play} Run Report
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Run Drawer ── */}
      {runDrawer && (
        <RunDrawer
          report={runDrawer}
          onClose={() => setRunDrawer(null)}
          onRun={handleRun}
        />
      )}
    </div>
  );
};

export default Report;
